import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

/**
 * ZAgentSkills — the per-agent FIRST-PARTY skill enablement surface. Drives the
 * "pre-built skills" section of the agent-builder Skills tab.
 *
 * Not to be confused with the org-CUSTOM code-interpreter skills
 * (`/api/code-interpreter-skills`, Prisma `CodeInterpreterSkill`, one S3 ZIP per
 * agent). Those are content an org uploads; these are platform skills the
 * platform ships, and enabling one MINTS A SCOPED API KEY bound to the agent
 * and injects it into that agent's sandbox.
 *
 * Routes:
 *   GET    /api/agents/:agentId/first-party-skills            — catalog + this agent's state
 *   PUT    /api/agents/:agentId/first-party-skills/:skillSlug — enable (idempotent)
 *   DELETE /api/agents/:agentId/first-party-skills/:skillSlug — disable + revoke the key
 *
 * Authorization: org-scoped via JWT + `CurrentOrganizationId`. The use cases
 * verify the agent belongs to the calling org before reading or writing and
 * surface 404 (never 403) for an agent in another org, so ids cannot be
 * enumerated across orgs — same rule as `ZAgentWorkspaces`.
 *
 * The raw minted key NEVER appears in a response. `apiKeyHint` is the masked
 * display value (`PublicApiKey.keyHint`); the plaintext key crosses only into
 * the agent's sandbox env.
 */

/** Why a catalog entry cannot be enabled on this particular agent. */
export const FIRST_PARTY_SKILL_UNAVAILABLE_REASONS = [
  "feature_disabled",
  "requires_code_interpreter"
] as const;

export const FirstPartySkillUnavailableReasonSchema = z.enum(FIRST_PARTY_SKILL_UNAVAILABLE_REASONS);

/**
 * One catalog entry, projected with this agent's state. The catalog copy
 * (title/description/label) is served rather than duplicated in the frontend so
 * adding a skill is a single change in `FIRST_PARTY_SKILL_CATALOG`.
 */
const FirstPartySkillStateSchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  /** Label for the separate "may create new projects" checkbox. */
  createGrantLabel: z.string().min(1),
  enabled: z.boolean(),
  /** The create grant. Meaningless while `enabled` is false; always `false` then. */
  canCreateGitProjects: z.boolean(),
  /** False when the org lacks the skill's feature flag, or the agent has no code interpreter. */
  available: z.boolean(),
  /** Non-null exactly when `available` is false. */
  unavailableReason: FirstPartySkillUnavailableReasonSchema.nullable(),
  /**
   * Masked hint of the key minted for this agent (e.g. `nxs_u_...a3f4`), or
   * null while disabled. Never the raw key — that only reaches the sandbox env.
   */
  apiKeyHint: z.string().nullable(),
  /**
   * The Vibe apps this agent's key may reach, as `VibeApp` ids. Empty while
   * disabled, and empty is a REAL answer for an enabled skill: the agent can
   * read no app and push to none.
   *
   * Projected from the `ResourcePermission` rows naming the minted key, never
   * from a column — so what the UI shows is the same set the authorization
   * check reads, and the two cannot drift.
   */
  vibeAppIds: z.array(z.string().uuid())
});

export const AgentSkillsPathVarsSchema = z.object({
  agentId: z.string().uuid()
});

export const AgentSkillSlugPathVarsSchema = AgentSkillsPathVarsSchema.extend({
  skillSlug: z.string().min(1).max(128)
});

/**
 * How many apps one enablement may name.
 *
 * A bound rather than a product limit: the reconcile writes one grant row per
 * id inside a single request, so an unbounded array is an unbounded write. Set
 * far above any plausible selection — an org with more Vibe apps than this
 * wants a role, not a hand-picked list.
 */
export const MAX_AGENT_SKILL_VIBE_APPS = 200;

export const EnableAgentSkillBodySchema = z.object({
  /**
   * Opt-in create grant. `false` mints a read-only key. Sent on every enable so
   * a PUT is a full statement of intent — re-enabling with a different value
   * re-mints the key rather than leaving a stale grant in place.
   */
  canCreateGitProjects: z.boolean(),
  /**
   * The Vibe apps this agent may reach — the "select repositories" half of the
   * least-privilege model, the create grant above being the "permissions" half.
   *
   * A FULL statement of intent, exactly like `canCreateGitProjects`: the set is
   * reconciled against what is stored, so an id dropped from this array has its
   * grant revoked. Sending the same array twice is a no-op.
   *
   * **Defaults to empty, and empty means the agent reaches NO app.** That is the
   * fail-closed direction and it is deliberate: a caller that omits the field —
   * an older client, a script written against the previous contract — must not
   * silently be granted the whole organization, which is what this field exists
   * to stop.
   */
  vibeAppIds: z.array(z.string().uuid()).max(MAX_AGENT_SKILL_VIBE_APPS).default([])
});

const SkillsResponseSchema = z.object({
  skills: z.array(FirstPartySkillStateSchema)
});

/**
 * Enable answers with the catalog AND with what it silently removed.
 *
 * A selected id naming a SOFT-DELETED app is dropped rather than refused —
 * refusing wedges the agent, because changing anything requires round-tripping
 * the whole selection and the dead id travels with it. Dropping is only safe
 * while the caller is TOLD, though: narrowing a selection the admin just
 * submitted and answering a plain 200 is a failed narrowing reporting success.
 * So the ids leave in the response and the UI can name them.
 *
 * Its own schema rather than a field on the shared one: List and Disable drop
 * nothing, and an always-empty array on them would be a question nobody asked.
 */
const EnableSkillsResponseSchema = SkillsResponseSchema.extend({
  droppedVibeAppIds: z.array(z.string().uuid())
});

export const ZAgentSkills = {
  /** GET /api/agents/:agentId/first-party-skills — the catalog with this agent's state. */
  List: {
    method: "GET" as const,
    path: "/api/agents/:agentId/first-party-skills",
    PathVars: AgentSkillsPathVarsSchema,
    Response: ApiSuccessSchema(SkillsResponseSchema)
  },

  /**
   * PUT /api/agents/:agentId/first-party-skills/:skillSlug — enable the skill.
   *
   * Idempotent by (agentId, skillSlug). A live key is KEPT and re-scoped in
   * place — its id is what the app grants and the running sandbox both name —
   * so re-issuing with a different `canCreateGitProjects` or a different
   * `vibeAppIds` narrows or widens the SAME credential. Only an absent, revoked
   * or expired key is re-minted.
   */
  Enable: {
    method: "PUT" as const,
    path: "/api/agents/:agentId/first-party-skills/:skillSlug",
    PathVars: AgentSkillSlugPathVarsSchema,
    Body: EnableAgentSkillBodySchema,
    Response: ApiSuccessSchema(EnableSkillsResponseSchema)
  },

  /**
   * DELETE /api/agents/:agentId/first-party-skills/:skillSlug — disable.
   *
   * Revokes the minted key and deletes the enablement row. Idempotent: a
   * disable on an already-disabled skill returns the list unchanged.
   */
  Disable: {
    method: "DELETE" as const,
    path: "/api/agents/:agentId/first-party-skills/:skillSlug",
    PathVars: AgentSkillSlugPathVarsSchema,
    Response: ApiSuccessSchema(SkillsResponseSchema)
  }
} as const;
